import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { 
  Building2, DollarSign, Calendar, Clock, Bed, Users,
  TrendingUp, ChevronLeft, ChevronRight, LogOut, Download, FileSpreadsheet, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HotelPortal = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeTab, setActiveTab] = useState('overview'); // overview, services, bookings
  const [exportLoading, setExportLoading] = useState(false);

  // Quick date range presets
  const [datePreset, setDatePreset] = useState('this_month');

  const applyDatePreset = (preset) => {
    const now = new Date();
    let from, to;
    
    switch (preset) {
      case 'today':
        from = to = now.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        from = to = yesterday.toISOString().split('T')[0];
        break;
      case 'this_week':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        from = startOfWeek.toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      case 'last_week':
        const lastWeekEnd = new Date(now);
        lastWeekEnd.setDate(now.getDate() - now.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        from = lastWeekStart.toISOString().split('T')[0];
        to = lastWeekEnd.toISOString().split('T')[0];
        break;
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        from = lastMonth.toISOString().split('T')[0];
        to = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'this_year':
        from = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        to = now.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    setDatePreset(preset);
    setDateFrom(from);
    setDateTo(to);
  };

  useEffect(() => {
    // Set default date range (current month)
    applyDatePreset('this_month');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchServices();
      fetchBookings();
    }
  }, [dateFrom, dateTo]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hotel/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API}/hotel/services?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Failed to load services');
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API}/hotel/bookings?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to load bookings');
      setBookings([]);
    }
  };

  // Export to Excel
  const exportToExcel = async (type) => {
    setExportLoading(true);
    try {
      const data = type === 'services' ? services : bookings;
      
      if (data.length === 0) {
        toast.error(`No ${type} to export`);
        return;
      }
      
      let worksheetData = [];
      let headers = [];
      
      if (type === 'services') {
        headers = ['Date', 'Customer', 'Service', 'Duration (mins)', 'Base Price', 'GST', 'Total Amount', 'Hotel Share', 'Company Share', 'Collected By', 'Status'];
        worksheetData = data.map(s => ({
          'Date': s.service_date || s.date,
          'Customer': s.customer_name,
          'Service': s.therapy_type,
          'Duration (mins)': s.duration || 60,
          'Base Price': s.base_price || 0,
          'GST': s.gst_amount || 0,
          'Total Amount': s.total_amount || 0,
          'Hotel Share': s.hotel_share || 0,
          'Company Share': s.company_share || (s.total_amount - s.hotel_share) || 0,
          'Collected By': s.payment_received_by,
          'Status': s.status || 'completed'
        }));
        
        // Add summary row
        const totalBase = data.reduce((sum, s) => sum + (s.base_price || 0), 0);
        const totalGst = data.reduce((sum, s) => sum + (s.gst_amount || 0), 0);
        const totalAmount = data.reduce((sum, s) => sum + (s.total_amount || 0), 0);
        const totalHotelShare = data.reduce((sum, s) => sum + (s.hotel_share || 0), 0);
        
        worksheetData.push({});
        worksheetData.push({
          'Date': 'TOTAL',
          'Customer': '',
          'Service': `${data.length} services`,
          'Duration (mins)': '',
          'Base Price': totalBase,
          'GST': totalGst,
          'Total Amount': totalAmount,
          'Hotel Share': totalHotelShare,
          'Company Share': totalAmount - totalHotelShare,
          'Collected By': '',
          'Status': ''
        });
      } else {
        headers = ['Date', 'Time', 'Customer', 'Phone', 'Room', 'Service', 'Duration (mins)', 'Amount', 'Status', 'Therapist'];
        worksheetData = data.map(b => ({
          'Date': b.booking_date,
          'Time': b.start_time,
          'Customer': b.guest_name,
          'Phone': b.guest_phone || '-',
          'Room': b.room_name || '-',
          'Service': b.therapy_type,
          'Duration (mins)': b.duration || 60,
          'Amount': b.total_amount,
          'Status': b.status,
          'Therapist': b.therapist_name || '-'
        }));
      }
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(worksheetData);
      
      // Set column widths
      const colWidths = headers.map(h => ({ wch: Math.max(h.length + 2, 12) }));
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, type === 'services' ? 'Sales Report' : 'Bookings Report');
      
      // Generate filename
      const propertyName = (dashboard?.property?.name || 'Hotel').replace(/\s+/g, '_');
      const filename = `${propertyName}_${type}_${dateFrom}_to_${dateTo}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Generate time slots
  const timeSlots = useMemo(() => {
    if (!dashboard?.property) return [];
    const slots = [];
    const opening = parseInt(dashboard.property.opening_time?.split(':')[0] || '9');
    const closing = parseInt(dashboard.property.closing_time?.split(':')[0] || '21');
    for (let hour = opening; hour < closing; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  }, [dashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Failed to load dashboard</p>
          <Button onClick={onLogout}>Logout</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream">
      {/* Header */}
      <div className="glass border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Building2 className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-xl font-serif text-foreground">{dashboard.property.name}</h1>
              <p className="text-sm text-muted-foreground">{dashboard.property.city} • Hotel Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Welcome, {user?.full_name || user?.email}
            </span>
            <Button variant="outline" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardDescription>Today's Services</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{dashboard.today.services}</p>
              <p className="text-sm text-muted-foreground">{dashboard.today.date}</p>
            </CardContent>
          </Card>
          
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardDescription>Today's Revenue</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{formatCurrency(dashboard.today.revenue)}</p>
              <p className="text-sm text-green-600">
                Your Share: {formatCurrency(dashboard.today.hotel_share)}
              </p>
            </CardContent>
          </Card>
          
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardDescription>Month Services</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{dashboard.month.services}</p>
            </CardContent>
          </Card>
          
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardDescription>Month Revenue</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{formatCurrency(dashboard.month.revenue)}</p>
              <p className="text-sm text-green-600">
                Your Share: {formatCurrency(dashboard.month.hotel_share)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Room Status & Today's Bookings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Room Status */}
          <div className="glass rounded-2xl p-6">
            <h3 className="text-lg font-serif text-foreground mb-4 flex items-center gap-2">
              <Bed className="w-5 h-5" /> Room Status
            </h3>
            
            <div className="space-y-3">
              {dashboard.rooms.map(room => (
                <div 
                  key={room.id} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    room.status === 'available' ? 'bg-green-50 border border-green-200' :
                    room.status === 'maintenance' ? 'bg-gray-100 border border-gray-300' :
                    room.status === 'cleaning' ? 'bg-blue-50 border border-blue-200' :
                    'bg-amber-50 border border-amber-200'
                  }`}
                >
                  <div>
                    <p className="font-medium">{room.room_name}</p>
                    {room.status === 'maintenance' && room.maintenance_reason && (
                      <p className="text-xs text-muted-foreground">{room.maintenance_reason}</p>
                    )}
                  </div>
                  <Badge variant={
                    room.status === 'available' ? 'default' :
                    room.status === 'maintenance' ? 'destructive' :
                    'secondary'
                  }>
                    {room.status}
                  </Badge>
                </div>
              ))}
              
              {dashboard.rooms.length === 0 && (
                <p className="text-center py-4 text-muted-foreground">No rooms configured</p>
              )}
            </div>
          </div>

          {/* Today's Bookings */}
          <div className="glass rounded-2xl p-6">
            <h3 className="text-lg font-serif text-foreground mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Today's Bookings ({dashboard.today.bookings})
            </h3>
            
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {dashboard.today_bookings.length > 0 ? (
                dashboard.today_bookings.map(booking => {
                  const room = dashboard.rooms.find(r => r.id === booking.room_id);
                  return (
                    <div 
                      key={booking.id} 
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{booking.guest_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {booking.start_time} - {booking.end_time} • {room?.room_name || 'Room'}
                        </p>
                        <p className="text-xs text-muted-foreground">{booking.therapy_type}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(booking.total_amount)}</p>
                        <Badge variant={
                          booking.status === 'completed' ? 'default' :
                          booking.status === 'cancelled' ? 'destructive' :
                          'secondary'
                        } className="text-xs">
                          {booking.status}
                        </Badge>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center py-4 text-muted-foreground">No bookings today</p>
              )}
            </div>
          </div>
        </div>

        {/* Services History */}
        <div className="glass rounded-2xl p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h3 className="text-lg font-serif text-foreground flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Sales History
            </h3>
            
            <div className="flex flex-wrap items-center gap-2">
              {/* Date Presets */}
              <Select value={datePreset} onValueChange={applyDatePreset}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="last_week">Last Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="this_year">This Year</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Custom Date Range */}
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="w-[140px]"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="w-[140px]"
                />
              </div>
              
              {/* Export Button */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => exportToExcel('services')}
                disabled={exportLoading || services.length === 0}
                data-testid="export-services-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Customer</th>
                  <th className="text-left py-2">Service</th>
                  <th className="text-right py-2">Amount</th>
                  <th className="text-right py-2">Your Share</th>
                  <th className="text-left py-2">Collected By</th>
                </tr>
              </thead>
              <tbody>
                {services.length > 0 ? (
                  services.map(service => (
                    <tr key={service.id} className="border-b border-border/30">
                      <td className="py-2">{service.service_date}</td>
                      <td className="py-2">{service.customer_name}</td>
                      <td className="py-2">{service.therapy_type}</td>
                      <td className="py-2 text-right">{formatCurrency(service.total_amount)}</td>
                      <td className="py-2 text-right text-green-600 font-medium">
                        {formatCurrency(service.hotel_share)}
                      </td>
                      <td className="py-2">
                        <Badge variant={service.payment_received_by === 'hotel' ? 'default' : 'outline'}>
                          {service.payment_received_by}
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-muted-foreground">
                      No services found for selected date range
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {services.length > 0 && (
            <div className="mt-4 pt-4 border-t flex justify-between items-center">
              <span className="text-muted-foreground">
                Showing {services.length} services
              </span>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-xl font-bold">
                  {formatCurrency(services.reduce((sum, s) => sum + (s.total_amount || 0), 0))}
                </p>
                <p className="text-sm text-green-600">
                  Your Share: {formatCurrency(services.reduce((sum, s) => sum + (s.hotel_share || 0), 0))}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-sm text-muted-foreground">
        <p>Powered by WellNest Technologies</p>
        <p>View-only access • Contact admin for any changes</p>
      </div>
    </div>
  );
};

export default HotelPortal;
